import RedirectionIcon from '@cp/src/assets/icons/RedirectionIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import { getSanitizedEnumValue } from '@cp/src/utils/commonUtils';
import { Button, Tag, Typography } from '@navi/web-ui/lib/primitives';
import { TicketStatusMap } from '../../constants';
import { getTagColor } from '../../utils';
import CustomerDetailValue from './CustomerDetailValue';
import styles from './index.module.scss';
import cx from 'classnames';
import { interpolatePathParams } from '@cp/src/utils/interpolate';
import APP_ROUTES from '@cp/src/layout/Routes';
import { TAB_KEYS } from '@cp/src/pages/CaseDetails/constants';
import { ICustomerDetail } from '../../types';
import CellRendererShortNumber from '@cp/src/components/TableCellRenderer/CellRendererShortNumber';
import { useCallback } from 'react';

const CustomerDetail = (props: ICustomerDetail) => {
  const { ticket, loading, hideTicketStatus = false, containerClass } = props;
  const { customerName, currentDpd, outstandingAmount, customerReferenceId } =
    ticket?.customerDetails || {};

  const supportTicketRequest = ticket?.supportRequestDetails;
  const caseReferenceId = supportTicketRequest?.caseReferenceId;
  const businessVertical = supportTicketRequest?.businessVertical;

  const customerRedirectionHandler = useCallback(() => {
    const loanId = ticket?.loanAccountNumber;
    if (loanId && customerReferenceId) {
      const path = interpolatePathParams(APP_ROUTES.CASE_DETAIL.path, {
        selectedTab: TAB_KEYS.OVERVIEW,
        loanId: loanId,
        customerId: customerReferenceId,
        caseBusinessVertical: businessVertical,
        caseReferenceId: caseReferenceId
      });
      const pathName = window?.location?.pathname;
      const location = pathName?.includes('/new') ? `/new${path}` : path;
      window.open(location, '_blank');
    }
  }, [ticket?.loanAccountNumber, customerReferenceId]);

  return (
    <div className={cx(styles.customerDetails, containerClass)}>
      <div className={styles.customerDetailsHeader}>
        <Typography
          variant="h4"
          color="var(--navi-color-gray-c3)"
          className="max-w[200px] flex-[2]"
        >
          Customer
        </Typography>
        <Typography variant="h4" color="var(--navi-color-gray-c3)" className="flex-1">
          DPD
        </Typography>
        <Typography variant="h4" color="var(--navi-color-gray-c3)" className="flex-1">
          POS
        </Typography>
        {!hideTicketStatus ? (
          <Typography variant="h4" color="var(--navi-color-gray-c3)" className="flex-1">
            Status
          </Typography>
        ) : null}
      </div>

      <div className={styles.customerDetailsValue}>
        <CustomerDetailValue
          loading={loading}
          hideTicketStatus={hideTicketStatus}
          containerClass="flex-[2]"
        >
          {customerName ? (
            <Button
              variant="text"
              endAdornment={<RedirectionIcon />}
              onClick={customerRedirectionHandler}
              className="max-w[200px] flex-[2] justify-start"
            >
              <Tooltip placement="bottom" hiddenPadding={0} hideStrategy="referenceHidden">
                <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
                  <Typography
                    variant="h4"
                    color="var(--blue-base)"
                    className="truncate max-w-[150px] text-start"
                  >
                    {customerName}
                  </Typography>
                </TooltipTrigger>
                <TooltipContent className="tooltipWrapper">{customerName}</TooltipContent>
              </Tooltip>
            </Button>
          ) : (
            <div className="max-w-[200px] flex-[2]">-</div>
          )}
        </CustomerDetailValue>
        <CustomerDetailValue loading={loading} hideTicketStatus={hideTicketStatus}>
          <Typography variant="h4" color="var(--navi-color-gray-c1)" className="font-medium flex-1">
            {currentDpd ?? '-'}
          </Typography>
        </CustomerDetailValue>
        <CustomerDetailValue loading={loading} hideTicketStatus={hideTicketStatus}>
          <div className="flex-1">
            <CellRendererShortNumber
              value={outstandingAmount}
              isCurrency
              significantDigits={2}
              ellipsisWrapperClass={styles.ellipsisWrapperClass}
              tooltipPlacement="bottom"
            />
          </div>
        </CustomerDetailValue>

        {!hideTicketStatus ? (
          <CustomerDetailValue loading={loading} hideTicketStatus={hideTicketStatus}>
            <div className="flex-1">
              <Tag
                color={getTagColor(ticket?.status)}
                variant="transparent"
                label={getSanitizedEnumValue(TicketStatusMap, ticket?.status)}
              />
            </div>
          </CustomerDetailValue>
        ) : null}
      </div>
    </div>
  );
};

export default CustomerDetail;
